// Badge de status do certificado MTLS (§6.1): válido / expirando
// (<30d, com contagem de dias) / expirado / sem certificado / N.A.
import {
  CircleAlertIcon,
  MinusIcon,
  ShieldCheckIcon,
  ShieldXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { ProcessorCertificateStatus } from "@/types/admin-processor-config"

const panelPrefix = "pages.adminProcessorConfig"

// Ícone + paleta por status (semântica Pay2B).
const STATUS_STYLES: Record<
  ProcessorCertificateStatus,
  { Icon: typeof ShieldCheckIcon; className: string }
> = {
  VALID: {
    Icon: ShieldCheckIcon,
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  EXPIRING: {
    Icon: TriangleAlertIcon,
    className:
      "border-amber-200 bg-amber-50 text-amber-700",
  },
  EXPIRED: {
    Icon: ShieldXIcon,
    className: "border-red-200 bg-red-50 text-red-700",
  },
  NOT_DEFINED: {
    Icon: CircleAlertIcon,
    className: "border-zinc-200 bg-zinc-50 text-zinc-500",
  },
  NOT_APPLICABLE: {
    Icon: MinusIcon,
    className: "border-zinc-200 bg-zinc-50 text-zinc-400",
  },
}

type CertificateStatusBadgeProps = {
  status: ProcessorCertificateStatus
  // Dias até expirar (exibido só no EXPIRING).
  daysToExpire?: number | null
}

export const CertificateStatusBadge = ({
  status,
  daysToExpire,
}: CertificateStatusBadgeProps) => {
  const { t } = useTranslation()
  const { Icon, className } = STATUS_STYLES[status]

  const label =
    status === "EXPIRING" &&
    daysToExpire !== null &&
    daysToExpire !== undefined
      ? t(`${panelPrefix}.certificate.expiringInDays`, {
          days: daysToExpire,
        })
      : t(`${panelPrefix}.certificate.status.${status}`)

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 rounded-full border font-medium",
        className
      )}
    >
      <Icon className="size-3" aria-hidden="true" />
      {label}
    </Badge>
  )
}
